import { useState, useCallback, useEffect, useRef } from 'react';

interface UseDebouncedInputOptions {
  /** External value to sync with */
  value: string;
  /** Callback when debounced value changes */
  onChange: (value: string) => void;
  /** Debounce delay in ms (default: 250) */
  debounceMs?: number;
  /** How long to wait after typing before allowing external sync (default: 500) */
  typingTimeoutMs?: number;
}

interface UseDebouncedInputReturn {
  /** Local value for immediate UI updates */
  localValue: string;
  /** Handle input change - updates local state immediately */
  handleChange: (value: string) => void;
  /** Handle clear - resets both local and external value */
  handleClear: () => void;
}

/**
 * Hook for managing debounced input with external value sync.
 *
 * Prevents race conditions by:
 * 1. Using local state for immediate UI updates (smooth typing)
 * 2. Debouncing updates to parent
 * 3. Blocking external sync while user is actively typing
 *
 * @example
 * const { localValue, handleChange, handleClear } = useDebouncedInput({
 *   value: searchTerm,
 *   onChange: handleSearchChange,
 * });
 */
export const useDebouncedInput = ({
  value,
  onChange,
  debounceMs = 250,
  typingTimeoutMs = 500,
}: UseDebouncedInputOptions): UseDebouncedInputReturn => {
  const [localValue, setLocalValue] = useState(value);

  // Track if user is actively typing to prevent external sync from overriding
  const isTypingRef = useRef(false);
  const typingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Sync local value when external value changes (but not while user is typing)
  useEffect(() => {
    if (!isTypingRef.current && value !== localValue) {
      setLocalValue(value);
    }
  }, [value]);

  // Debounced onChange to parent
  useEffect(() => {
    const timer = setTimeout(() => {
      if (localValue !== value) {
        onChange(localValue);
      }
    }, debounceMs);

    return () => clearTimeout(timer);
  }, [localValue, debounceMs]);

  // Handle input change - update local state immediately for smooth UX
  const handleChange = useCallback((newValue: string) => {
    isTypingRef.current = true;
    setLocalValue(newValue);

    // Clear existing typing timer
    if (typingTimerRef.current) {
      clearTimeout(typingTimerRef.current);
    }

    // Reset typing flag after timeout (allows external sync when idle)
    typingTimerRef.current = setTimeout(() => {
      isTypingRef.current = false;
    }, typingTimeoutMs);
  }, [typingTimeoutMs]);

  // Handle clear
  const handleClear = useCallback(() => {
    setLocalValue('');
    isTypingRef.current = false;
    onChange('');
  }, [onChange]);

  return { localValue, handleChange, handleClear };
};
